/**
 * Display helpers for cron expressions (ADR-0072).
 *
 * Centralizes cronstrue + cron-parser usage so the four schedule list / detail
 * pages share a single rendering path. Also exports helpers for converting
 * between tenant-tz wall-clock components and UTC ISO timestamps — used by
 * the schedule editor so the user's typed datetime persists losslessly across
 * timezones.
 */

import cronstrue from "cronstrue";
import * as cronParserPkg from "cron-parser";

const { parseExpression } = (cronParserPkg as {
  parseExpression: typeof import("cron-parser").parseExpression;
} & {
  default?: { parseExpression: typeof import("cron-parser").parseExpression };
}).default ?? cronParserPkg;

/** Render a cron string as an English summary; falls back to the raw expression. */
export function cronToText(cron: string | null | undefined): string {
  if (!cron) return "—";
  try {
    return cronstrue.toString(cron, { use24HourTimeFormat: false, verbose: true });
  } catch {
    return cron;
  }
}

/**
 * Compute the next fire of a cron schedule in the tenant's timezone, honoring
 * effective-date and end-date bounds. Returns null if the schedule is past its
 * end_date or the cron is invalid.
 */
export function nextCronFire(
  cron: string | null | undefined,
  timezone: string,
  opts: { effectiveDate?: string | null; endDate?: string | null } = {}
): Date | null {
  if (!cron) return null;
  const now = new Date();
  if (opts.endDate && new Date(opts.endDate) < now) return null;
  const startFrom = opts.effectiveDate && new Date(opts.effectiveDate) > now
    ? new Date(opts.effectiveDate)
    : now;
  try {
    const expr = parseExpression(cron, { tz: timezone, currentDate: startFrom, iterator: false });
    const next = expr.next().toDate();
    if (opts.endDate && next > new Date(opts.endDate)) return null;
    return next;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Tenant-tz wall-clock <-> UTC ISO
// ---------------------------------------------------------------------------

interface LocalParts {
  year: number; month: number; day: number; hour: number; minute: number;
}

function partsInTz(d: Date, tz: string): LocalParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24,
    minute: get("minute"),
  };
}

function partsToMinutes(p: LocalParts): number {
  // A monotonic-ish encoding for offset arithmetic. Mixing month/day fields
  // works because we only ever compare close values that differ by an offset
  // ≤ 14h, so month/day rollover matches.
  return p.year * 525600 + p.month * 44640 + p.day * 1440 + p.hour * 60 + p.minute;
}

/**
 * Convert tenant-tz wall-clock components to a UTC ISO instant.
 *
 * Example: `wallClockToUtcISO("2026-04-27", "09:15", "America/New_York")`
 * during EDT → `"2026-04-27T13:15:00.000Z"` (the UTC instant at which the
 * local clock in New York reads 09:15 on 2026-04-27).
 *
 * Returns "" when inputs are blank or malformed.
 */
export function wallClockToUtcISO(dateStr: string, timeStr: string, tz: string): string {
  if (!dateStr || !timeStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, min] = timeStr.split(":").map(Number);
  if ([y, m, d, h, min].some((n) => !Number.isFinite(n))) return "";

  // First approximation: pretend wall-clock IS UTC. Then iterate to find the
  // UTC instant whose tz-localized representation matches the wall-clock.
  let candidate = new Date(Date.UTC(y, m - 1, d, h, min));
  const wantedMinutes = partsToMinutes({ year: y, month: m, day: d, hour: h, minute: min });
  for (let i = 0; i < 2; i++) {
    const local = partsInTz(candidate, tz);
    const drift = wantedMinutes - partsToMinutes(local);
    if (drift === 0) break;
    candidate = new Date(candidate.getTime() + drift * 60_000);
  }
  return candidate.toISOString();
}

/**
 * Convert a UTC ISO instant back to tenant-tz wall-clock components.
 *
 * Example: `utcISOToWallClock("2026-04-27T13:15:00.000Z", "America/New_York")`
 * during EDT → `{ date: "2026-04-27", time: "09:15" }`.
 */
export function utcISOToWallClock(
  iso: string | null | undefined,
  tz: string
): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const local = partsInTz(d, tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${local.year}-${pad(local.month)}-${pad(local.day)}`,
    time: `${pad(local.hour)}:${pad(local.minute)}`,
  };
}

/**
 * Render a UTC ISO instant as a human-readable timestamp in the given IANA
 * timezone. Returns "" when the input is blank or unparseable.
 *
 * Example: `formatUtcISOInTz("2026-04-27T13:15:00.000Z", "America/New_York")`
 * during EDT → `"2026-04-27 09:15"`.
 */
export function formatUtcISOInTz(
  iso: string | null | undefined,
  tz: string
): string {
  const wc = utcISOToWallClock(iso, tz);
  if (!wc.date) return "";
  return `${wc.date} ${wc.time}`;
}
